import { useTranslation } from "@i18n";
import React, { useState } from "react";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import UsersListInSpace from "./UsersListInSpace";
import type { SpaceUserRow } from "./UsersListInSpace";
import ToggleSwitchComponent from "../../../../../cubeComponent/form/ToggleSwitchComponent";
import Text from "@CubeComponents/html/Text/Text";

interface AddUserModalProps {
  /** Plain `string`: callers hand this down from route params / component props, unvalidated. */
  spaceId?: string;
  closeModal: () => void;
  addUserCallback: (selectedUsers: SpaceUserRow[], transitionUsers: boolean) => void;
  disabledUserIds?: string[];
  allowMultiSelection?: boolean;
  /**
   * When set, the footer offers a toggle that also assigns the picked users to linked transitions.
   * `null` is in the union because `ReviewFlowProperties` passes an explicit `null` to opt out.
   */
  checkBoxToAssignUserToTransition?: boolean | null;
}

function AddUserModal({
  spaceId,
  closeModal,
  addUserCallback,
  disabledUserIds = [],
  allowMultiSelection = true,
  checkBoxToAssignUserToTransition,
}: AddUserModalProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("Users");
  const [selectedUsers, setSelectedUsers] = useState<SpaceUserRow[]>([]);
  const [transitionUsers, setTransitionUsers] = useState(false);

  const selectUsers = (value: SpaceUserRow[]) => {
    setSelectedUsers(value);
  };

  const activeTabChangeHandler = (e: React.MouseEvent<HTMLElement>) => {
    const target = e.currentTarget.attributes["data-name"].value;
    setActiveTab(target);
  };

  const onSubmitAction = () => {
    addUserCallback(selectedUsers, transitionUsers);
  };

  const handleToogleSwitch = (args: React.ChangeEvent<HTMLInputElement>) => {
    setTransitionUsers(args.target.checked);
  };

  const header = () => (
    <div className="w-full rounded-t-1 px-8 py-4">
      <Text className="text-2xl font-bold">{t("Add Users")}</Text>
    </div>
  );

  const tabComponent = () => (
    <div className="flex items-center gap-8 border-b-2 border-[#ecf3fe] bg-white pb-0.5">
      <div data-name="Users" className="flex h-14 cursor-pointer flex-col justify-end" onClick={activeTabChangeHandler}>
        <Text className="text-base font-semibold leading-[2.75rem]">{t("Users")}</Text>
        <div
          className={
            activeTab === "Users"
              ? "h-1 rounded-md bg-[linear-gradient(96deg,var(--primary-gradient-color1)_0%,var(--primary-gradient-color2)_110%)]"
              : "h-1 bg-transparent"
          }
        />
      </div>

      {/* <div data-name="Groups" className={css.individualTab} onClick={activeTabChangeHandler}>
        <span>{t("Groups")}</span>
        <div className={activeTab === 'Groups' ? css.selectedTab : css.unselectedTab} />
      </div>

      <div data-name="Organisations" className={css.individualTab} onClick={activeTabChangeHandler}>
        <span>{t("Organisations")}</span>
        <div className={activeTab === 'Organisations' ? css.selectedTab : css.unselectedTab} />
      </div>

      <div data-name="Positions" className={css.individualTab} onClick={activeTabChangeHandler}>
        <span>{t("Positions")}</span>
        <div className={activeTab === 'Positions' ? css.selectedTab : css.unselectedTab} />
      </div> */}
    </div>
  );

  const content = () => (
    <div>
      {tabComponent()}
      <div className="py-1">
        {activeTab === "Users" && (
          <UsersListInSpace
            spaceId={spaceId}
            selectUsers={selectUsers}
            disabledUserIds={disabledUserIds}
            allowMultiSelection={allowMultiSelection}
          />
        )}
      </div>
    </div>
  );

  const footer = () => (
    <div className="flex gap-4">
      <div className="flex items-center gap-4">
        <ButtonComponent
          buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
          disabled={!selectedUsers.length}
          onClick={onSubmitAction}
        >
          <Text>{t("Add")}</Text>
        </ButtonComponent>

        <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={closeModal}>
          <Text>{t("Discard")}</Text>
        </ButtonComponent>

        {checkBoxToAssignUserToTransition && (
          <div className="flex items-center space-inline-2">
            <ToggleSwitchComponent
              id="toggleBtn"
              // checked={!!getSpaceToggle.toggle}
              onChange={handleToogleSwitch}
              data-testid="SpaceToggleButton"
            />
            <label htmlFor="toggleBtn" className="ms-2" data-testid="ShowHiddenItemsText">
              <Text>{t("Assign selected user(s) to associated transitions")}</Text>
            </label>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex h-[25rem] flex-col">
      <ModalStateDialogComponent
        width="75rem"
        headerTitle={header()}
        modelContent={content()}
        footerContent={footer()}
        allowModalPadding={false}
        onCancel={closeModal}
        isVisible
      />
    </div>
  );
}

export default AddUserModal;
